import { Box, IconButton, LinearProgress, Snackbar, Typography } from "@mui/material";
import { Check, Cross, Download, X, CircleAlert } from "lucide-react";
import "./Snackbar.scss";
import { useEffect, useState } from "react";
import variables from "../../../styles/variables.module.scss";

export enum SnackbarModes {
	SUCCESS = "success",
	DOWNLOAD = "download",
	ERROR = "error",
	CATEGORY = "category",
	WARNING = "warning",
}

export default function CustomSnackbar({
	open,
	onClose,
	title,
	subtitle,
	mode,
}: {
	open: boolean;
	onClose: () => void;
	title: string;
	subtitle: string;
	mode: SnackbarModes;
}) {
	const [progress, setProgress] = useState(0);
	const [selectProgress, setSelectProgress] = useState(0);

	useEffect(() => {
		let timer: NodeJS.Timeout;

		const startProgress = () => {
			const duration = 4900;
			const interval = 50;
			const increment = 100 / (duration / interval);

			timer = setInterval(() => {
				setProgress((prev) => {
					if (prev >= 100) {
						return 0;
					}
					return prev + increment;
				});
			}, interval);
		};

		startProgress();

		return () => clearInterval(timer);
	}, []);

	useEffect(() => {
		if (!open || mode !== SnackbarModes.CATEGORY) return;

		let timer: NodeJS.Timeout;
		const duration = 20000;
		const interval = 50;
		const increment = 100 / (duration / interval);

		setSelectProgress(0);

		timer = setInterval(() => {
			setSelectProgress((prev) => {
				const next = prev + increment;
				if (next >= 100) {
					clearInterval(timer);
					onClose();
					return 100;
				}
				return next;
			});
		}, interval);

		return () => clearInterval(timer);
	}, [open, mode, onClose]);

	return (
		<Snackbar
			open={open}
			autoHideDuration={mode === SnackbarModes.CATEGORY ? null : 5000}
			anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
			onClose={onClose}
			message={title}
		>
			<Box
				className={`snackbar ${
					mode === SnackbarModes.CATEGORY ? "snackbar--select-nurse" : ""
				}`}
				sx={{
					backgroundColor:
						mode === SnackbarModes.DOWNLOAD
							? "#F5EFF6"
							: mode === SnackbarModes.SUCCESS
							? "#EEF9DC"
							: mode === SnackbarModes.CATEGORY || mode === SnackbarModes.WARNING
							? variables.warning100
							: "#F9DEDC",
					border: `solid 1px ${
						mode === SnackbarModes.DOWNLOAD
							? "#6D247A33"
							: mode === SnackbarModes.SUCCESS
							? "#B4C992"
							: mode === SnackbarModes.CATEGORY || mode === SnackbarModes.WARNING
							? variables.warning300
							: "#DBDBDB"
					}`,
				}}
			>
				<Box
					className={`snackbar__content ${
						mode === SnackbarModes.CATEGORY ? "snackbar--select-nurse__content" : ""
					}`}
				>
					{mode === SnackbarModes.DOWNLOAD ? (
						<Download
							size={25}
							style={{ color: "#6D247A" }}
						/>
					) : mode === SnackbarModes.SUCCESS ? (
						<Check
							size={25}
							style={{ color: "#608C18" }}
						/>
					) : mode === SnackbarModes.CATEGORY || mode === SnackbarModes.WARNING ? (
						<CircleAlert
							size={mode === SnackbarModes.CATEGORY ? 45 : 25}
							style={{ color: variables.warning300 }}
						/>
					) : (
						<X
							size={25}
							style={{ color: "#B3261E" }}
						/>
					)}
					<Box className="snackbar__content__text">
						<Typography
							className="snackbar__content__text-title"
							style={
								mode === SnackbarModes.DOWNLOAD
									? { color: "#6D247A" }
									: mode === SnackbarModes.SUCCESS
									? { color: "#608C18" }
									: mode === SnackbarModes.CATEGORY ||
									  mode === SnackbarModes.WARNING
									? { color: variables.warning300 }
									: { color: "#B3261E" }
							}
						>
							{title}
						</Typography>
						<Typography className="snackbar__content__text-subtitle">
							{subtitle}
						</Typography>
					</Box>
					{mode === SnackbarModes.CATEGORY && (
						<X
							size={45}
							className="snackbar--select-nurse__close"
							onClick={onClose}
						/>
					)}
				</Box>
				<LinearProgress
					className="snackbar__linear"
					sx={{
						backgroundColor:
							mode === SnackbarModes.DOWNLOAD
								? "#6D247A33"
								: mode === SnackbarModes.SUCCESS
								? "#B4C992"
								: mode === SnackbarModes.CATEGORY || mode === SnackbarModes.WARNING
								? variables.warning300
								: "#EFBEBB",
						"& .MuiLinearProgress-bar": {
							backgroundColor:
								mode === SnackbarModes.DOWNLOAD
									? "#6D247A"
									: mode === SnackbarModes.SUCCESS
									? "#608C18"
									: mode === SnackbarModes.CATEGORY ||
									  mode === SnackbarModes.WARNING
									? variables.warning200
									: "#B3261E",
						},
					}}
					variant={mode === SnackbarModes.DOWNLOAD ? "indeterminate" : "determinate"}
					value={
						mode === SnackbarModes.DOWNLOAD
							? undefined
							: mode === SnackbarModes.CATEGORY
							? selectProgress
							: progress
					}
				/>
			</Box>
		</Snackbar>
	);
}
